import { useState, useEffect, useCallback } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import {
  LayoutDashboard, ShoppingCart, Users, Package, CreditCard, ListChecks,
  BarChart3, Settings, Bell, LogOut, Menu, X, Radio, CheckCheck,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { logoSrc } from "@/lib/nav";
import { formatDateTime } from "@/lib/format";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/orders", label: "Orders", icon: ShoppingCart },
  { to: "/customers", label: "Customers", icon: Users },
  { to: "/plans", label: "Plans", icon: Package },
  { to: "/payments", label: "Payments", icon: CreditCard },
  { to: "/tasks", label: "Tasks", icon: ListChecks },
  { to: "/reports", label: "Reports", icon: BarChart3 },
  { to: "/settings", label: "Settings", icon: Settings },
];

const STAFF_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/orders", label: "My Orders", icon: ShoppingCart },
  { to: "/tasks", label: "My Tasks", icon: ListChecks },
];

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });

  const load = useCallback(async () => {
    try {
      const res = await api.get("/notifications");
      setData(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const markAll = async () => {
    await api.post("/notifications/read-all");
    load();
  };

  return (
    <DropdownMenu onOpenChange={(o) => o && load()}>
      <DropdownMenuTrigger asChild>
        <button data-testid="notification-bell" className="relative h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100 transition-colors">
          <Bell className="h-5 w-5 text-slate-600" />
          {data.unread > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold grid place-items-center">
              {data.unread > 9 ? "9+" : data.unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <span className="font-semibold text-sm text-slate-900">Notifications</span>
          {data.unread > 0 && (
            <button onClick={markAll} data-testid="mark-all-read" className="text-xs text-blue-700 inline-flex items-center gap-1 hover:underline">
              <CheckCheck className="h-3.5 w-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto">
          {data.items.length === 0 && <p className="px-4 py-8 text-center text-sm text-slate-500">No notifications yet.</p>}
          {data.items.map((n) => (
            <div key={n.id} className={cn("px-4 py-3 border-b last:border-0", !n.read && "bg-blue-50/50")}>
              <div className="flex items-start gap-2">
                {!n.read && <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />}
                <div className={cn(!n.read ? "" : "pl-3.5")}>
                  <p className="text-sm font-medium text-slate-900">{n.title}</p>
                  <p className="text-xs text-slate-600 mt-0.5">{n.message}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{formatDateTime(n.created_at)}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function DashboardLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = user?.role === "admin" ? ADMIN_NAV : STAFF_NAV;
  const logo = logoSrc(user);

  const doLogout = async () => { await logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside className={cn(
        "fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#0f172a] flex flex-col transition-transform lg:translate-x-0",
        mobileOpen ? "translate-x-0" : "-translate-x-full"
      )}>
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-slate-800">
          {logo ? (
            <img src={logo} alt="logo" className="h-9 w-9 rounded-lg object-cover bg-white shrink-0" />
          ) : (
            <div className="h-9 w-9 rounded-lg bg-blue-600 grid place-items-center text-white font-bold font-display shrink-0">
              {(user?.business_name || "V").slice(0, 1).toUpperCase()}
            </div>
          )}
          <div className="min-w-0">
            <div className="text-white font-bold font-display leading-tight truncate">{user?.business_name || "VYOMRA CRM"}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">Powered by VYOMRA</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          {nav.map((item) => (
            <NavLink key={item.to} to={item.to} data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) => cn("sidebar-link", isActive && "active")}>
              <item.icon className="h-4.5 w-4.5" size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-800">
          <button onClick={doLogout} data-testid="logout-button" className="sidebar-link w-full">
            <LogOut className="h-4.5 w-4.5" size={18} /> Sign out
          </button>
        </div>
      </aside>

      {mobileOpen && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 sm:px-6 sticky top-0 z-20">
          <div className="flex items-center gap-3">
            <button className="lg:hidden h-9 w-9 grid place-items-center rounded-lg hover:bg-slate-100" onClick={() => setMobileOpen(true)}>
              <Menu className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Radio className="h-4 w-4 text-blue-600" />
              <span className="capitalize font-medium text-slate-700">{user?.role} Panel</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <div className="flex items-center gap-2.5 pl-2 border-l">
              <div className="h-9 w-9 rounded-full bg-blue-600 text-white grid place-items-center font-semibold text-sm">
                {(user?.name || "U").slice(0, 1).toUpperCase()}
              </div>
              <div className="hidden sm:block">
                <div className="text-sm font-medium text-slate-900 leading-none">{user?.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{user?.email}</div>
              </div>
            </div>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 max-w-[1400px] w-full mx-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
